import { useState, useCallback } from "react";
import axiosInstance from "./axiosConfig";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

interface UseApiHookParams {
  endpoint: string;
  method: HttpMethod;
  config?: AxiosRequestConfig;
  body?: any;
  pagination?: any;
  filters?: any;
}

interface FetchDataArgs {
  payload?: any;
  endPointQuery?: string;
  callback?: () => void;
}

const useAxios = <T>({ endpoint, method, config = {}, body, pagination, filters }: UseApiHookParams) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationData, setPaginationData] = useState<string | null>(null);

  const fetchData = useCallback(
    async ({ payload, endPointQuery, callback }: FetchDataArgs = {}) => {
      try {
        setLoading(true);
        setError(null);

        const url = endpoint + (endPointQuery || "");

        const headers = {
          ...config.headers,
          ...(pagination ? { "x-pagination": JSON.stringify(pagination) } : {}),
          ...(filters ? { "x-filters": JSON.stringify(filters) } : {}),
        };

        const requestConfig: AxiosRequestConfig = { ...config, headers };

        let response: AxiosResponse<T>;

        switch (method) {
          case "GET":
            response = await axiosInstance.get(url, requestConfig);
            break;
          case "POST":
            response = await axiosInstance.post(url, payload || body, requestConfig);
            break;
          case "PATCH":
            response = await axiosInstance.patch(url, payload || body, requestConfig);
            break;
          case "PUT":
            response = await axiosInstance.put(url, payload || body, requestConfig);
            break;
          case "DELETE":
            response = await axiosInstance.delete(url, { ...requestConfig, data: payload || body });
            break;
          default:
            throw new Error("Unsupported HTTP method");
        }

        setData(response.data);

        const paginationHeader = response.headers["x-pagination"];
        if (paginationHeader) setPaginationData(paginationHeader);

        callback && callback();
        return response.data;
      } catch (err: any) {
        setError(err?.response?.data || "An error occurred");
        setData(null);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [endpoint, method, body, pagination, filters, config]
  );

  return { data, loading, error, fetchData, paginationData };
};

export default useAxios;
